/**
 * CategoryPopover Component
 * Filtered popover for #category selection in the quick-add input.
 * Shows defaults first, then custom categories. Offers creation if no match.
 */

import { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Hash, X, Plus } from 'lucide-react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useTaskCategories, useCreateCategory, useHideCategory } from '@/hooks/queries';

export interface CategoryPopoverProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (categoryId: string, categoryName: string) => void;
  searchQuery: string;
  anchorRef: React.RefObject<HTMLInputElement | null>;
}

export interface CategoryPopoverRef {
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

interface Measurable {
  getBoundingClientRect(): DOMRect;
}

export const CategoryPopover = forwardRef(function CategoryPopover(
  props: CategoryPopoverProps,
  ref: React.ForwardedRef<CategoryPopoverRef>
) {
  const { open, onOpenChange, onSelect, searchQuery, anchorRef } = props;
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const categoriesRead = useTaskCategories(undefined);
  const categories = categoriesRead.status === 'ready' ? categoriesRead.data : [];
  const { mutateAsync: createCategory } = useCreateCategory();
  const { mutate: hideCategory } = useHideCategory();

  const query = searchQuery.toLowerCase();
  const filtered = categories
    .filter((c) => !query || c.name.toLowerCase().includes(query))
    .slice(0, 8);

  // Check if query could create a new category
  const queryTrimmed = searchQuery.trim();
  const hasExactMatch = filtered.some(
    (c) => c.name.toLowerCase() === queryTrimmed.toLowerCase()
  );
  const showCreateOption = queryTrimmed.length > 0 && !hasExactMatch;
  const totalItems = filtered.length + (showCreateOption ? 1 : 0);

  // Reset highlight when query or open state changes
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchQuery, open]);

  // Clamp highlighted index
  useEffect(() => {
    if (highlightedIndex >= totalItems) {
      setHighlightedIndex(Math.max(0, totalItems - 1));
    }
  }, [totalItems, highlightedIndex]);

  const handleCreate = useCallback(async () => {
    try {
      const result = await createCategory(queryTrimmed);
      onSelect(result.id, result.name);
    } catch {
      // If creation fails, just close
      onOpenChange(false);
    }
  }, [createCategory, queryTrimmed, onSelect, onOpenChange]);

  const handleHide = useCallback(
    (e: React.MouseEvent, categoryId: string) => {
      e.stopPropagation();
      e.preventDefault();
      hideCategory(categoryId);
    },
    [hideCategory]
  );

  // Keyboard handler -- called from the parent input's onKeyDown
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open || totalItems === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHighlightedIndex((i) => Math.min(i + 1, totalItems - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHighlightedIndex((i) => Math.max(i - 1, 0));
          break;
        case 'Enter':
          e.preventDefault();
          e.stopPropagation();
          if (highlightedIndex < filtered.length) {
            const cat = filtered[highlightedIndex];
            onSelect(cat.id, cat.name);
          } else if (showCreateOption) {
            handleCreate();
          }
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange(false);
          break;
      }
    },
    [open, totalItems, filtered, highlightedIndex, onSelect, showCreateOption, handleCreate, onOpenChange]
  );

  useImperativeHandle(ref, () => ({ handleKeyDown }), [handleKeyDown]);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor virtualRef={anchorRef as React.RefObject<Measurable>} />
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-56 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
        onKeyDown={handleKeyDown}
      >
        <div className="py-1 max-h-[240px] overflow-y-auto">
          {filtered.map((category, index) => (
            <button
              key={category.id}
              type="button"
              onClick={() => onSelect(category.id, category.name)}
              onMouseEnter={() => setHighlightedIndex(index)}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-2 text-sm cursor-pointer',
                'hover:bg-accent transition-colors',
                index === highlightedIndex && 'bg-accent'
              )}
            >
              <Hash className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
              <span className="flex-1 text-left truncate font-medium">
                {category.name}
              </span>
              {category.isDefault ? (
                <span className="text-[0.6rem] text-muted-foreground">default</span>
              ) : (
                <button
                  type="button"
                  onClick={(e) => handleHide(e, category.id)}
                  className="text-muted-foreground/50 hover:text-muted-foreground p-0.5 rounded"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </button>
          ))}

          {/* Create option */}
          {showCreateOption && (
            <button
              type="button"
              onClick={handleCreate}
              onMouseEnter={() => setHighlightedIndex(filtered.length)}
              className={cn(
                'flex items-center gap-2 w-full px-3 py-2 text-sm cursor-pointer',
                'hover:bg-accent transition-colors border-t border-border',
                highlightedIndex === filtered.length && 'bg-accent'
              )}
            >
              <Plus className="h-3.5 w-3.5 text-primary shrink-0" />
              <span className="text-left truncate text-primary font-medium">
                Create #{queryTrimmed}
              </span>
            </button>
          )}

          {filtered.length === 0 && !showCreateOption && (
            <div role="status" className="px-3 py-2 text-sm text-muted-foreground">
              {categoriesRead.status === 'ready'
                ? 'No categories'
                : categoriesRead.status === 'failed'
                  ? "Couldn't load categories. Try again shortly."
                  : categoriesRead.status === 'offline'
                    ? "You're offline, so categories can't load."
                    : 'Loading categories…'}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
});
